import { Box, Typography, Backdrop, CircularProgress } from "@mui/material";
import { DesktopDateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { useCallback, useEffect, useState } from "react";
import { getPatientData } from "../../../../Api/AdapterApi";
import { parseDateTime } from "../../../../utils/helpers";
import toast from "react-hot-toast";
import RoomStatisticsChart from "../../../../Components/RoomStatisticsChartComponent";

const RoomStatistics = ({
  patientId,
  defaultStart,
  defaultEnd,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  const fetchRoomData = useCallback(async () => {
    if (!patientId || !startTime || !endTime) return;
    toast.remove();
    try {
      const params = {
        patient_id: patientId,
        start: parseDateTime(startTime),
        end: parseDateTime(endTime),
      };
      setLoading(true);
      const response = await getPatientData(params);
      if (response) {
        const pointCount = response.byteLength / 40;
        const timestampOffset      = 0;
        const temperatureOffset    =  timestampOffset      + pointCount * 6;
        const humidityOffset       =  temperatureOffset    + pointCount * 4; 
        const illumOffset          =  humidityOffset       + pointCount * 4; 
        const uvOffset             =  illumOffset          + pointCount * 4; 
        const rOffset              =  uvOffset             + pointCount * 2; 
        const gOffset              =  rOffset              + pointCount * 4; 
        const bOffset              =  gOffset              + pointCount * 4; 
        const brOffset             =  bOffset              + pointCount * 4; 
        const hrOffset             =  brOffset             + pointCount * 1; 
        const micAmplOffset        =  hrOffset             + pointCount * 1; 
        const vitalsPresenceOffset =  micAmplOffset        + pointCount * 2; 
        const vitalsMovementOffset =  vitalsPresenceOffset + pointCount * 1; 
        const vitalsDistanceOffset =  vitalsMovementOffset + pointCount * 1; 

        const decodedData = {
          pointCount,
          timestamp:      new DataView(response, timestampOffset,      6 * pointCount),
          temperature:    new DataView(response, temperatureOffset,    4 * pointCount),
          humidity:       new DataView(response, humidityOffset,       4 * pointCount),
          illum:          new DataView(response, illumOffset,          4 * pointCount),
          uv:             new DataView(response, uvOffset,             2 * pointCount),
          r:              new DataView(response, rOffset,              4 * pointCount),
          g:              new DataView(response, gOffset,              4 * pointCount),
          b:              new DataView(response, bOffset,              4 * pointCount),
          br:             new DataView(response, brOffset,             1 * pointCount),
          hr:             new DataView(response, hrOffset,             1 * pointCount),
          micAmpl:        new DataView(response, micAmplOffset,        2 * pointCount),
          vitalsPresence: new DataView(response, vitalsPresenceOffset, 1 * pointCount),
          vitalsMovement: new DataView(response, vitalsMovementOffset, 1 * pointCount),
          vitalsDistance: new DataView(response, vitalsDistanceOffset, 2 * pointCount),
        };

        decodedData.getTimestamp = i => {
          let hour = decodedData.timestamp.getUint8(6*i);
          const minute = decodedData.timestamp.getUint8(6*i + 1);
          const month = decodedData.timestamp.getUint8(6*i + 2);
          const day = decodedData.timestamp.getUint8(6*i + 3);
          const year = decodedData.timestamp.getUint16(6*i + 4, true);
          let period = hour >= 12 ? "PM" : "AM";
          
          if (hour === 0) {
            hour = 12;
          } else if (hour > 12) {
            hour -= 12;
          }

          return `${hour}:${minute} ${period} (${month}/${day}/${year})`;
        };

        decodedData.getTemperature = i => decodedData.temperature.getFloat32(4*i, true);
        decodedData.getHumidity = i => decodedData.humidity.getFloat32(4*i, true);
        decodedData.getIllum = i => decodedData.illum.getUint32(4*i, true);
        decodedData.getUv = i => decodedData.uv.getUint16(2*i, true);
        decodedData.getR = i => decodedData.r.getUint32(4*i, true);
        decodedData.getG = i => decodedData.g.getUint32(4*i, true);
        decodedData.getB = i => decodedData.b.getUint32(4*i, true);
        decodedData.getBr = i => decodedData.br.getUint8(i);
        decodedData.getHr = i => decodedData.hr.getUint8(i);
        decodedData.getMicAmpl = i => decodedData.micAmpl.getUint16(2*i, true);
        decodedData.getVitalsPresence = i => decodedData.vitalsPresence.getUint8(i);
        decodedData.getVitalsMovement = i => decodedData.vitalsMovement.getUint8(i);
        decodedData.getVitalsDistance = i => decodedData.vitalsDistance.getUint16(2*i, true);

        setData(decodedData);
      }
    } catch (error) {
      console.log("Error fetching progress data:", error);
      
      if ("reason" in error) {
        toast.error(error.reason);
      } else {
        toast.error("Error loading data");
      }
    }
    setLoading(false);
  }, [patientId, startTime, endTime]);

  useEffect(() => {
    fetchRoomData();
  }, [fetchRoomData]);

  return (
    <Box
      className="main-card"
      sx={{
        display: "flex",
        flexDirection: "column",
        mt: "24px",
        pb: "24px",
        gap: "12px",
      }}
    >
      <Box className="main-card-header">
        <Typography component="h4" className="text-primery text--left">
          Raw Data
        </Typography>
        <Box display="flex" gap="20px">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box className="select-option">
              <DesktopDateTimePicker
                disableFuture
                format="yyyy/MM/dd hh:mm"
                onChange={setStartTime}
                value={startTime}
                closeOnSelect={false}
              />
            </Box>
            <Box className="select-option">
              <DesktopDateTimePicker
                disableFuture
                format="yyyy/MM/dd hh:mm"
                onChange={setEndTime}
                value={endTime}
                closeOnSelect={false}
              />
            </Box>
          </LocalizationProvider>
        </Box>
      </Box>
      {!data && !loading && (
        <Box
          display="flex"
          alignItems="center"
          flex={1}
          position="relative"
          justifyContent="center"
        >
          <Typography component="h4" className="text-primery" align="center" pb="24px">
            {!startTime || !endTime ? "Select dates" : "No data"}
          </Typography>
        </Box>
      )}
      {loading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            // flex: 1,
            position: "relative",
            justifyContent: "center",
            gap: "24px",
          }}
        >
          <Typography component="h4" className="text-primery" align="center">
            Loading
          </Typography>
          <CircularProgress sx={{ color: "#91D9B9" }} />
        </Box>
      )}
      {data && (
        <>
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getTemperature,    label: "Average Temperature (°C)", unit: " °C", decimals: 2 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getHumidity,       label: "Average Humidity (% rel)", unit: "",    decimals: 2 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getIllum,          label: "Max Illuminance",          unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getUv,             label: "Max UV",                   unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getR,              label: "Max Red",                  unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getG,              label: "Max Green",                unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getB,              label: "Max Blue",                 unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getBr,             label: "Average Breath Rate",      unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getHr,             label: "Average Heart Rate",       unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getMicAmpl,        label: "Max Audio Amplitude",      unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getVitalsPresence, label: "Presence",                 unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getVitalsMovement, label: "Mav Radar Movement",       unit: "",    decimals: 0 }} />
          <RoomStatisticsChart data={{ pointCount: data.pointCount, xGetter: data.getTimestamp, yGetter: data.getVitalsDistance, label: "Average Radar Distance",   unit: "",    decimals: 0 }} />
        </>
      )}
    </Box>
  );
};

export default RoomStatistics;
